import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "../../../../assets/media/icons";
import Cross from "../../../../assets/media/icons/Cross";
import { CurrencyContext } from "../../../../contexts/currency";
import { useAnonymousApi } from "../../../../hooks/useApi";
import { gtag } from "../../../../utils/track";
import Flex from "./../../../structure/Flex";
import { trackZostelEvent } from "../../../../utils/tracking";
interface AvailabilityCalendarProps {
  inventoryId: number;
  operatorCode: string;
  checkInDate: string;
}

const INCREMENT: number = window.innerWidth < 768 ? 4 : 7;

type Price = { base_price: number; units: number; date: string };

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  inventoryId,
  operatorCode,
  checkInDate,
}) => {
  const api = useAnonymousApi("STAY_OFFERED_PRICING");
  const [appender, setAppender] = useState<number>(0);
  const [prices, setPrices] = useState<Price[]>([]);
  const history = useHistory();

  const { formatCurrency } = useContext(CurrencyContext);

  const startDate = useMemo(
    () => moment(checkInDate).add(INCREMENT * appender, "days"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appender]
  );

  const goToDate = (date: moment.Moment) => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("checkin")) {
      searchParams.set("checkin", date.format("YYYY-MM-DD"));
    } else {
      searchParams.append("checkin", date.format("YYYY-MM-DD"));
      gtag("event", "AvailabilityCalendarSelect_Date", { value: date });
      trackZostelEvent("AvailabilityCalendarSelect_Date", { value: date });
    }
    history.push(`${window.location.pathname}?${searchParams.toString()}`);
  };

  useEffect(() => {
    setAppender(0);
  }, [inventoryId, operatorCode, checkInDate]);

  useEffect(() => {
    if (api) {
      setPrices([]);
      api
        .get(
          `/?checkin=${startDate.format("YYYY-MM-DD")}&checkout=${startDate
            .clone()
            .add(INCREMENT, "days")
            .format(
              "YYYY-MM-DD"
            )}&property_code=${operatorCode}&room_ids=${inventoryId}`
        )
        .then((res) => {
          // console.log("Availability Calendar", res.data);
          const _prices: Price[] = Array.from({ length: INCREMENT }).map(
            (v, i) => {
              return {
                date: startDate.clone().add(i, "days").format("YYYY-MM-DD"),
                base_price: 0,
                units: 0,
              };
            }
          );
          res.data.availability.forEach((a: any) => {
            const p = _prices.find((p) => p.date === a.date);
            if (p) {
              p.units = a.bookable && a.units > 0 ? a.units : 0;
            }
          });
          res.data.offered_rooms?.[0]?.pricing.forEach((a: any) => {
            const p = _prices.find((p) => p.date === a.date);
            if (p) {
              p.base_price = a.offered_price || a.base_price || 0;
            }
          });
          // console.log(_prices);
          setPrices(_prices);
        })
        .catch(console.log);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, appender]);

  return (
    <Flex className="relative w-full px-4 bg-white border-t rounded-b-lg">
      {Array.from({ length: INCREMENT }).map((v, i) => {
        const available = prices[i] && prices[i].units > 0;
        const date = startDate.clone().add(i, "days");
        return (
          <Flex
            col
            items="center"
            className={`px-2 py-4 flex-1 ${
              available ? "hover:bg-gray-200 cursor-pointer" : ""
            }`}
            key={i}
            onClick={available ? goToDate.bind(null, date) : undefined}
          >
            <span className="text-xs tracking-wide text-gray-600 uppercase">
              {date.format("ddd")}
            </span>
            <span className="text-sm font-medium text-gray-600">
              {date.format("MMM DD")}
            </span>
            {prices.length === 0 ? (
              <>
                <span className="mt-3 mb-4 font-medium">...</span>
              </>
            ) : available ? (
              <>
                <span className="mt-2 font-medium text-green-500">
                  {formatCurrency(prices[i].base_price)}
                </span>
                <span className="text-sm font-medium text-gray-800">
                  {prices[i].units} units
                </span>
              </>
            ) : (
              <span className="flex flex-col items-center justify-center mt-3 font-medium text-gray-700">
                <Cross className="w-4 h-4" />
                <span className="mt-1 text-sm font-medium text-gray-700">
                  No units
                </span>
              </span>
            )}
          </Flex>
        );
      })}
      {moment(startDate)
        .clone()
        .subtract(INCREMENT, "days")
        .format("YYYY-MM-DD") >= moment().format("YYYY-MM-DD") && (
        <Flex items="center" className="absolute top-0 bottom-0 left-0">
          <button
            className="p-2 text-white rounded-r-lg bg-orange focus:outline-none"
            onClick={setAppender.bind(null, (v) => v - 1)}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        </Flex>
      )}
      <Flex items="center" className="absolute top-0 bottom-0 right-0">
        <button
          className="p-2 text-white rounded-l-lg bg-orange focus:outline-none"
          onClick={setAppender.bind(null, (v) => v + 1)}
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      </Flex>
    </Flex>
  );
};

export default AvailabilityCalendar;
